import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
    Observable,
    catchError,
    delay,
    exhaustMap,
    filter,
    first,
    map,
    of,
    retry,
    switchMap,
    take,
    tap,
    timeout,
} from 'rxjs';
import { Md5 } from 'ts-md5';

import {
    B2cAuthenticationService,
    B2cUserInfoService,
    B2cUserprofileService,
} from '@mobi/b2c-authentication-ng-jslib';
import { CurrentUserInfo as SharedCurrentUserInfo } from '@mobi/mcs-schadenmeldung-types-ng-jslib';
import { MobiUiLanguage, MobiUiLanguageService } from '@mobi/rwc-ui-components-ng-jslib';
import { FeatureToggleService, LanguageService, Logger } from '@mobi/rwc-utils-ng-jslib';

import { LogoutService } from '../../core/oauth/logout.service';
import { GeocodeService } from '../../features/osm/shared/service/geocode.service';
import { McsSchadenmeldungIntegrationService } from '../../features/osm/shared/service/mcs-schadenmeldung-integration.service';
import { McsSchadenmeldungUnifiedIntegrationService } from '../../features/osm/shared/service/mcs-schadenmeldung-unified-integration.service';
import { GaOrganisationUnit } from '../../features/osm/shared/service/mcs-schadenmeldung-unified-integration.service.types';
import * as PersonalienActions from '../../features/osm/shared/state/actions/personalien.actions';
import { NavigationService } from '../../service/navigation.service';
import { GET_CURRENT_USER_TOGGLE } from '../../shared/constants/feature-toggle.constants';
import { SESSION_STORAGE, removeAllSessionStorage } from '../../shared/constants/storage.constants';
import {
    addMessage,
    changeCoreLanguage,
    checkLoginStateFromServiceEvent,
    contactGeocodedFromEffect,
    contactLoadedSuccessAction,
    detectCoreLanguage,
    gaContactLoadedSuccess,
    gaSelectedFromDropdown,
    generalContactLoadedSuccess,
    initState,
    loadContactFromPublicModule,
    loadUserSuccess,
    loginFromHeader,
    loginStateCheckedFromEffect,
    logoutFromHeader,
    navigateAwayFromEffect,
    osmAuthenticatedModuleLoaded,
    removeMessage,
    removeMessagesCreatedBy,
    replaceMessage,
} from '../actions/core.actions';
import { CurrentUserInfo, LOT, Language } from '../models/core.types';
import * as fromCore from '../selectors/core.selectors';
import { EMPTY_ACTION } from '../state.util';

import { selectLoadContactParams, selectLoginParams } from './core.effects.selectors';

@Injectable()
export class CoreEffects {
    private actions$ = inject(Actions);
    private languageService = inject(LanguageService);
    private schadenmeldungIntegrationService = inject(McsSchadenmeldungIntegrationService);
    private schadenmeldungUnifiedIntegrationService = inject(
        McsSchadenmeldungUnifiedIntegrationService,
    );
    private featureToggleService = inject(FeatureToggleService);
    private authenticationService = inject(B2cAuthenticationService);
    private b2cUserprofileService = inject(B2cUserprofileService);
    private b2cUserInfoService = inject(B2cUserInfoService);
    private b2cLanguageService = inject(MobiUiLanguageService);
    private navigationService = inject(NavigationService);
    private geocodeService = inject(GeocodeService);
    private store = inject(Store);
    private logger = inject(Logger);
    private logoutService = inject(LogoutService);

    private delayRemoveMessage = 4_950;
    private authenticationTimeoutMs = 10_000;

    checkLoginStateFromServiceEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(checkLoginStateFromServiceEvent),
            concatLatestFrom(() => [
                this.authenticationService.isAuthenticated(),
                this.b2cUserprofileService.getActiveProfile(),
                this.b2cUserInfoService.getUserInfo(),
            ]),
            map(([_, loggedIn, profile, loginUserData]) =>
                loginStateCheckedFromEffect({ loggedIn, profile, loginUserData }),
            ),
        );
    });

    login$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(PersonalienActions.login, loginFromHeader),
            concatLatestFrom(() => this.store.select(selectLoginParams)),
            tap(([_, params]) => this.navigationService.rememberStepToRedirectAfterLogin(params)),
            tap(() => this.authenticationService.login()),
            switchMap(() => EMPTY_ACTION),
        );
    });

    removeSessionStorageAndReinitState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(logoutFromHeader, navigateAwayFromEffect),
            tap(() => removeAllSessionStorage()),
            map(() => initState()),
        );
    });

    performLogout$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(logoutFromHeader),
            delay(300), // wait for other effects to be completed
            switchMap(() => this.logoutService.logout()),
            switchMap(() => EMPTY_ACTION),
        );
    });

    navigateAway$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(navigateAwayFromEffect),
            delay(300), // wait for other effects to be completed
            tap(({ url }) => this.navigationService.locationHref(url)),
            switchMap(() => EMPTY_ACTION),
        );
    });

    loadUser$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(osmAuthenticatedModuleLoaded),
            concatLatestFrom(() => [
                this.store.select(fromCore.selectLanguage),
                this.store.select(fromCore.selectLot),
            ]),
            // /users/current must only be loaded for fully authenticated (LOT 3) users.
            filter(([, , levelOfTrust]) => levelOfTrust === LOT.STRONG_AUTH_3),
            switchMap(([, language]) => this.loadCurrentUserReturningEmptyWhenFailed(language)),
            map(currentUser => {
                if (currentUser?.user?.userId) {
                    const hash = Md5.hashStr(currentUser.user.userId) + '';
                    const before = sessionStorage.getItem(SESSION_STORAGE.USER_ID_HASH.key);
                    sessionStorage.setItem(SESSION_STORAGE.USER_ID_HASH.key, hash);
                    if (before && before !== hash) {
                        sessionStorage.removeItem(SESSION_STORAGE.NGRX_STORE_OSM.key);
                        return loadUserSuccess({ currentUser, reInitState: true });
                    }
                }
                return loadUserSuccess({ currentUser });
            }),
        );
    });

    loadUserSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(loadUserSuccess),
            filter(({ currentUser }) => !!currentUser),
            concatLatestFrom(() => this.store.select(fromCore.selectLanguage)),
            switchMap(([{ currentUser }, language]) =>
                this.featureToggleService.getTreatment$(GET_CURRENT_USER_TOGGLE).pipe(
                    take(1),
                    switchMap(toggle =>
                        toggle === 'on'
                            ? this.schadenmeldungUnifiedIntegrationService
                                  .loadContact(language)
                                  .pipe(
                                      catchError(error => {
                                          this.logger.warn(`could not load contact`, error);
                                          return of(undefined);
                                      }),
                                  )
                            : of((currentUser as SharedCurrentUserInfo)?.contact),
                    ),
                ),
            ),
            map(contact => generalContactLoadedSuccess({ contact })),
        );
    });

    loadUserSuccessReinitState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(loadUserSuccess),
            switchMap(({ reInitState }) => (reInitState ? of(initState()) : EMPTY_ACTION)),
        );
    });

    loadContactPublic$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(gaSelectedFromDropdown, loadContactFromPublicModule),
            concatLatestFrom(() => this.store.select(selectLoadContactParams)),
            filter(([__, { protekta }]) => !protekta), // no contact for protekta
            map(([action, { language, identifikation }]) => [
                (action as any).gaId || identifikation?.partnerGa,
                language,
            ]),
            filter(([gaId]) => !!gaId),
            switchMap(([gaId, language]) =>
                this.featureToggleService.getTreatment$(GET_CURRENT_USER_TOGGLE).pipe(
                    take(1),
                    switchMap(toggle =>
                        (toggle === 'on'
                            ? this.schadenmeldungUnifiedIntegrationService
                            : this.schadenmeldungIntegrationService
                        ).loadContactPublic(gaId, language),
                    ),
                ),
            ),
            map(contact => gaContactLoadedSuccess({ contact })),
        );
    });

    contactLoadedSuccessAction$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(...contactLoadedSuccessAction.all),
            filter(
                ({ contact }) =>
                    !!contact?.organisationUnit && !contact?.organisationUnit?.geoLocation,
            ),
            switchMap(({ contact }) => this.geocodeAddress(contact.organisationUnit)),
        );
    });

    detectCoreLanguage$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(detectCoreLanguage),
            exhaustMap(() => this.languageService.language$.pipe(take(1))),
            map(language => {
                if (language === 'en') {
                    this.logger.warn(
                        '[CORE-EFFECTS] English is not supported, will not use it, will fall back to german',
                    );
                    return 'de';
                }
                return language;
            }),
            map(language => changeCoreLanguage({ language })),
        );
    });

    changeCoreLanguage$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(changeCoreLanguage),
            tap(({ language }) => {
                this.languageService.use(language);
                this.b2cLanguageService.setLanguage(language as MobiUiLanguage);
            }),
            switchMap(() => EMPTY_ACTION),
        );
    });

    replaceMessageRemove$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(replaceMessage),
            map(({ message }) => removeMessagesCreatedBy({ origin: message.origin })),
        );
    });

    replaceMessageAdd$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(replaceMessage),
            map(({ message }) => addMessage({ message })),
        );
    });

    addMessage$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(addMessage),
            switchMap(({ message }) =>
                message.fadeOut
                    ? of(removeMessage({ message })).pipe(delay(this.delayRemoveMessage))
                    : EMPTY_ACTION,
            ),
        );
    });

    private loadCurrentUserReturningEmptyWhenFailed(
        language: Language,
    ): Observable<CurrentUserInfo | undefined> {
        return this.whenAuthenticated$().pipe(
            switchMap(authenticated =>
                authenticated
                    ? this.getCurrentUserForActiveToggle(language).pipe(
                          retry(3), // we need it as other calls might fail with missing partnerNummer
                      )
                    : of(undefined),
            ),
            catchError(error => {
                this.logger.warn(`could not load user`, error);
                return of(undefined);
            }),
        );
    }

    private whenAuthenticated$(): Observable<boolean> {
        return this.authenticationService.isAuthenticated$().pipe(
            first(Boolean, false), // first `true`, or `false` if the stream completes without one
            timeout({ first: this.authenticationTimeoutMs, with: () => of(false) }),
        );
    }

    private getCurrentUserForActiveToggle(language: Language): Observable<CurrentUserInfo> {
        return this.featureToggleService.getTreatment$(GET_CURRENT_USER_TOGGLE).pipe(
            take(1),
            switchMap(toggle =>
                (toggle === 'on'
                    ? this.schadenmeldungUnifiedIntegrationService
                    : this.schadenmeldungIntegrationService
                ).getCurrentUser(language),
            ),
        );
    }

    private geocodeAddress(organisationUnit: GaOrganisationUnit) {
        const { street, postalCode, location } = organisationUnit.addresses?.address ?? {};
        const query = [street, postalCode, location].filter(token => !!token).join(' ');
        return query
            ? this.geocodeService.geocodeAddress(query).pipe(
                  map(({ location }) => contactGeocodedFromEffect({ geoLocation: location })),
                  catchError(error => {
                      this.logger.warn(`error geocoding ${query}`, error);
                      return EMPTY_ACTION;
                  }),
              )
            : EMPTY_ACTION;
    }
}
